import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useMemo } from 'react';
import { Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { buildApiUrl } from '../../utils/apiSetup';

const MONTH_COLS = [
  { recKey: 'advApr', advKey: 'inApr', monthKey: '4' },
  { recKey: 'advMay', advKey: 'inMay', monthKey: '5' },
  { recKey: 'advJun', advKey: 'inJun', monthKey: '6' },
  { recKey: 'advJul', advKey: 'inJul', monthKey: '7' },
  { recKey: 'advAug', advKey: 'inAug', monthKey: '8' },
  { recKey: 'advSep', advKey: 'inSep', monthKey: '9' },
  { recKey: 'advOct', advKey: 'inOct', monthKey: '10' },
  { recKey: 'advNov', advKey: 'inNov', monthKey: '11' },
  { recKey: 'advDec', advKey: 'inDec', monthKey: '12' },
  { recKey: 'advJan', advKey: 'inJan', monthKey: '1' },
  { recKey: 'advFeb', advKey: 'inFeb', monthKey: '2' },
  { recKey: 'advMar', advKey: 'inMar', monthKey: '3' },
];

const AdvReportView = () => {
  const { hasPermission } = useAuth();
  const currentYear = new Date().getFullYear();
  // Default to current financial year
  const initialFy = new Date().getMonth() >= 3 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
  
  const [financialYear, setFinancialYear] = useState(initialFy);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate options for the last 5 financial years and next 2
  const fyOptions = useMemo(() => {
    const options = [];
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      options.push(`${i}-${i + 1}`);
    }
    return options;
  }, [currentYear]);

  // Derive start and end years based on selected financial year
  const startYear = parseInt(financialYear.split('-')[0]);
  const endYear = parseInt(financialYear.split('-')[1]);

  useEffect(() => {
    const controller = new AbortController();
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(buildApiUrl(`/api/v1/reports/adv-report?financial_year=${financialYear}`), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to load PF Adv report data');
        }
        const data = await response.json();
        setReportData(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(err);
          setError(err.message || 'Error loading Adv report');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => controller.abort();
  }, [financialYear]);

  const rows = reportData?.rows || [];
  const summary = reportData?.summary || {};

  const handleExport = () => {
    if (!rows || rows.length === 0) return;

    // Format data for excel to match the columns
    const excelData = rows.map((row, idx) => ({
      'Sr. No': idx + 1,
      'Code No.': row.codeNo,
      'NAME': row.name,
      'Designation': row.designation,
      'Area': row.area,
      'Balance': row.balance,
      [`Apr-${startYear.toString().slice(2)}`]: row.advApr || 0,
      [`Adv IN April ${startYear}`]: row.inApr || 0,
      [`May-${startYear.toString().slice(2)}`]: row.advMay || 0,
      [`Adv IN May ${startYear}`]: row.inMay || 0,
      [`Jun-${startYear.toString().slice(2)}`]: row.advJun || 0,
      [`Adv IN June ${startYear}`]: row.inJun || 0,
      [`Jul-${startYear.toString().slice(2)}`]: row.advJul || 0,
      [`Adv IN July ${startYear}`]: row.inJul || 0,
      [`Aug-${startYear.toString().slice(2)}`]: row.advAug || 0,
      [`Adv IN Aug ${startYear}`]: row.inAug || 0,
      [`Sep-${startYear.toString().slice(2)}`]: row.advSep || 0,
      [`Adv IN Sept. ${startYear}`]: row.inSep || 0,
      [`Oct-${startYear.toString().slice(2)}`]: row.advOct || 0,
      [`Adv IN Oct ${startYear}`]: row.inOct || 0,
      [`Nov-${startYear.toString().slice(2)}`]: row.advNov || 0,
      [`Adv IN Nov ${startYear}`]: row.inNov || 0,
      [`Dec-${startYear.toString().slice(2)}`]: row.advDec || 0,
      [`Adv IN DEC ${startYear}`]: row.inDec || 0,
      [`Jan-${endYear.toString().slice(2)}`]: row.advJan || 0,
      [`ADV IN JAN ${endYear}`]: row.inJan || 0,
      [`Feb-${endYear.toString().slice(2)}`]: row.advFeb || 0,
      [`Adv IN Feb ${endYear}`]: row.inFeb || 0,
      [`Mar-${endYear.toString().slice(2)}`]: row.advMar || 0,
      [`Adv IN Mar ${endYear}`]: row.inMar || 0,
      'TOTAL ADV': row.totalAdv || 0,
      'Amt. recovered': row.amtRecovered || 0,
      'Balance (Final)': row.finalBalance || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Adv Report');
    
    // Save file
    XLSX.writeFile(workbook, `Adv_Report_${financialYear}.xlsx`);
  };

  const getThClass = (isRed = false) => `border border-gray-300 px-3 py-2 text-xs font-semibold text-center whitespace-nowrap bg-white sticky top-0 ${isRed ? 'text-red-600' : 'text-gray-700'}`;
  const getTdClass = (isNumeric = true) => `border border-gray-300 px-3 py-2 text-xs whitespace-nowrap ${isNumeric ? 'text-right' : 'text-left'}`;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Filters and Actions */}
      <div className="p-4 border-b border-gray-200 flex flex-wrap gap-4 items-end justify-between bg-gray-50">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Financial Year
            </label>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[140px]"
            >
              {fyOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          {hasPermission('report:export') && (
            <button
              onClick={handleExport}
              disabled={loading || !reportData || rows.length === 0}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="m-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Table Container */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
            <p className="text-gray-500 font-medium text-sm">Loading Adv report data...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-base font-medium mb-1">No Adv report records found</p>
            <p className="text-xs text-gray-400">Please verify selected financial year.</p>
          </div>
        ) : (
          <div className="inline-block min-w-max align-middle border border-gray-200 rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className={getThClass()}>Sr. No</th>
                  <th className={getThClass()}>Code No.</th>
                  <th className={getThClass()}>NAME</th>
                  <th className={getThClass()}>Designation</th>
                  <th className={getThClass()}>Area</th>
                  <th className={getThClass()}>Balance</th>
                  
                  <th className={getThClass()}>Apr-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>April {startYear}</th>
                  
                  <th className={getThClass()}>May-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>May {startYear}</th>
                  
                  <th className={getThClass()}>Jun-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>June {startYear}</th>
                  
                  <th className={getThClass()}>Jul-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>July {startYear}</th>
                  
                  <th className={getThClass()}>Aug-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>Aug {startYear}</th>
                  
                  <th className={getThClass()}>Sep-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>Sept. {startYear}</th>
                  
                  <th className={getThClass()}>Oct-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>Oct {startYear}</th>
                  
                  <th className={getThClass()}>Nov-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>Nov {startYear}</th>
                  
                  <th className={getThClass()}>Dec-{startYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>DEC {startYear}</th>
                  
                  <th className={getThClass()}>Jan-{endYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>ADV IN<br/>JAN {endYear}</th>
                  
                  <th className={getThClass()}>Feb-{endYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>Feb {endYear}</th>
                  
                  <th className={getThClass()}>Mar-{endYear.toString().slice(2)}</th>
                  <th className={getThClass(true)}>Adv IN<br/>Mar {endYear}</th>
                  
                  <th className={getThClass()}>TOTAL<br/>ADV</th>
                  <th className={getThClass()}>Amt.<br/>recovered</th>
                  <th className={getThClass()}>Balance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row, idx) => (
                  <tr key={row.codeNo || idx} className="hover:bg-gray-50">
                    <td className={getTdClass()}>{idx + 1}</td>
                    <td className={getTdClass(false)}>{row.codeNo}</td>
                    <td className={getTdClass(false)}>{row.name}</td>
                    <td className={getTdClass(false)}>{row.designation}</td>
                    <td className={getTdClass(false)}>{row.area}</td>
                    <td className={getTdClass()}>{row.balance ? row.balance.toLocaleString('en-IN') : 0}</td>
                    
                    <td className={getTdClass()}>{row.advApr ? row.advApr.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inApr ? row.inApr.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advMay ? row.advMay.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inMay ? row.inMay.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advJun ? row.advJun.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inJun ? row.inJun.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advJul ? row.advJul.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inJul ? row.inJul.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advAug ? row.advAug.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inAug ? row.inAug.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advSep ? row.advSep.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inSep ? row.inSep.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advOct ? row.advOct.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inOct ? row.inOct.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advNov ? row.advNov.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inNov ? row.inNov.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advDec ? row.advDec.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inDec ? row.inDec.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advJan ? row.advJan.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inJan ? row.inJan.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advFeb ? row.advFeb.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inFeb ? row.inFeb.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.advMar ? row.advMar.toLocaleString('en-IN') : ''}</td>
                    <td className={getTdClass()}>{row.inMar ? row.inMar.toLocaleString('en-IN') : ''}</td>
                    
                    <td className={getTdClass()}>{row.totalAdv ? row.totalAdv.toLocaleString('en-IN') : 0}</td>
                    <td className={getTdClass()}>{row.amtRecovered ? row.amtRecovered.toLocaleString('en-IN') : 0}</td>
                    <td className={getTdClass()}>{row.finalBalance ? row.finalBalance.toLocaleString('en-IN') : 0}</td>
                  </tr>
                ))}

                {/* Summary row */}
                <tr className="bg-white font-bold sticky bottom-0 z-10 border-t-2 border-gray-300">
                  <td colSpan={5} className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">TOTAL</td>
                  <td className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">
                    {(summary.balance || 0).toLocaleString('en-IN')}
                  </td>
                  
                  {MONTH_COLS.map(({ monthKey }) => {
                    const yearNum = parseInt(monthKey) >= 4 ? startYear : endYear;
                    const fullKey = `${monthKey}-${yearNum}`;
                    const recTot = summary.monthly_rec_totals?.[fullKey] || 0;
                    const advTot = summary.monthly_adv_totals?.[fullKey] || 0;

                    return (
                      <React.Fragment key={fullKey}>
                        <td className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">
                          {recTot ? recTot.toLocaleString('en-IN') : ''}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">
                          {advTot ? advTot.toLocaleString('en-IN') : ''}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  
                  <td className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">
                    {(summary.totalAdv || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">
                    {(summary.amtRecovered || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-xs text-right bg-gray-50">
                    {(summary.finalBalance || 0).toLocaleString('en-IN')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvReportView;
